import Icon, {
    CaretDownOutlined,
    CaretUpOutlined,
    CopyOutlined,
    MinusOutlined,
    PlusCircleFilled,
    PlusOutlined
} from "@ant-design/icons";
import {Button, Collapse, Tooltip} from "antd";
import classNames from "classnames";
import React, {useEffect, useRef, useState} from "react";
import {useHistory, useLocation} from "react-router-dom";
import EditableLabel from "../../components/EditableLabel";
import {Close} from "../../components/cohort-queries/icons/Close";
import {CohortTimeframe} from "../../components/cohort-timeframe/CohortTimeframe";
import ToolTip, {ADD_CRITERIA, ADD_EVENT, EVENT, INDEX_EVENT} from "../../components/common/ToolTip";
import {Delete} from "../../components/common/actions/Delete";
import buttonStyles from "../../components/common/styled/Buttons.module.scss";
import {useAppDispatch, useAppSelector} from "../../hooks";
import {calculateIndexMap} from "../../reducers/util";
import {Criterion, Group as GroupType, Query} from "../HealthcareUsageReport/types/CohortQuery";
import Operator from "../Operator";
import styles from './EditStudyContent.module.scss';
import {TimeBetweenEvents} from "./TimeBetweenEvents/TimeBetweenEvents";
import {
    addCriterion,
    addGroup,
    copyGroup,
    criterionOperatorChanged,
    deleteCriterion,
    deleteGroup,
    groupOperatorChanged,
    resetCriteria,
    selectActiveKeys,
    setActiveKey,
    setCurrentCriterion,
    setGroupTitle
} from "./cohortBuilderSlice";
import DefaultMessage from "./TimeBetweenEvents/DefaultMessage";

const { Panel } = Collapse

interface CriterionProps {
    id: string
    criterionId: string
    groupId: string
    queryId: number
    description: string | undefined
    isInclusion: boolean
    operator: string[]
}

export const CriterionView = ({ criterionId, groupId, queryId, description, isInclusion }: CriterionProps) => {
    const dispatch = useAppDispatch();
    const history = useHistory();
    const path = useLocation().pathname;

    const [isExpanded, setIsExpanded] = useState(false);
    const maxCharLimit = 400;
    const toggleExpand = () => setIsExpanded(!isExpanded);

    const displayDescription = description && description.length > maxCharLimit && !isExpanded
        ? `${description.slice(0, maxCharLimit)}...`
        : description;

    return (
        <div
            className={classNames(styles.criterion, isInclusion ? styles.inclusion : styles.exclusion)}
            onClick={(event) => {
                event.stopPropagation();
                dispatch(setCurrentCriterion({ queryId, groupId, criterionId }));
                history.push(`${path}/criteria`);
            }}
        >
            {description === undefined ? (
                <div className={styles["new-criteria"]}>Click here to Define Criteria</div>
            ) : (
                <>
                    <div className={styles.descriptionContainer}>
                        <div className="title" dangerouslySetInnerHTML={{ __html: displayDescription }} />

                        {description.length > maxCharLimit && (
                            <div className={styles.expandCollapseButtonContainer}>
                                {isExpanded ? (
                                    <button
                                        data-testid="collapse-button"
                                        className={styles.titleButton}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            toggleExpand();
                                        }}
                                    >
                                        <CaretUpOutlined className={styles.icon} />
                                    </button>
                                ) : (
                                    <button
                                        data-testid="expand-button"
                                        className={styles.titleButton}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            toggleExpand();
                                        }}
                                    >
                                        <CaretDownOutlined className={styles.icon} />
                                    </button>
                                )}
                            </div>
                        )}
                    </div>
                </>
            )}

            <Icon
                component={Close}
                style={{ fontSize: '1.8em' }}
                onClick={(event) => {
                    event.stopPropagation();
                    dispatch(deleteCriterion({ queryId, groupId, criterionId }));
                }}
            />
        </div>
    );
};


interface GroupProps {
    id: string
    queryId: number
    criteria: Criterion[]
    deleteDisabled?: boolean
}

const GroupView = ({ id, queryId, criteria, deleteDisabled }: GroupProps) => {

    const dispatch = useAppDispatch()

    const isInclusion = (index: number) => {
        const previous = criteria[index - 1]
        return (index === 0) || !previous.operator.includes("not")
    }

    const criteriaDisplay = criteria
        .map((c, index: number) => <CriterionView id={c.id} key={c.id} criterionId={c.id} groupId={id} queryId={queryId} description={c.description} isInclusion={isInclusion(index)} operator={c.operator} />)
        .flatMap(i =>
            [
                i,
                <Operator key={i.props.id + '_'} currentValue={i.props.operator} showChildren={false} showIcons={false}
                                    onOperatorChanged={() => {
                                        return (value: string) => {
                                            dispatch(criterionOperatorChanged({
                                                queryId: queryId,
                                                groupId: id,
                                                criterionId: i.props.id,
                                                value
                                            }))
                                        }
                                    }}
                                    isBetweenEvents={false}/>
            ])
    // remove the last operator
    criteriaDisplay.pop()

    return <span>
        <div key="body" className={styles.body}>
            {
                criteriaDisplay
            }
        </div>
        <div key="footer" className="footer">
            <span data-testid="add-criterion" className="clickable" onClick={() => dispatch(addCriterion({ queryId: queryId, groupId: id }))} >
                <PlusCircleFilled style={{ fontSize: '1em', color: '#565656' }} /> Add criterion<ToolTip content={ADD_CRITERIA} style={{ padding: "0 0 0 2px" }} />
            </span>
            <Tooltip title="Copy Event">
                <CopyOutlined
                    style={{ fontSize: '1em', color: '#565656' }}
                    onClick={() => dispatch(copyGroup({ queryId: queryId, groupId: id }))} />
            </Tooltip>
            {deleteDisabled ? null : <Delete icon="s" tooltip={"Delete Event"} onDelete={() => { dispatch(deleteGroup({ queryId: queryId, groupId: id })) }} />}
        </div>
    </span>
}

const GenExtra: React.FC<{ isViewIndex: boolean }> = ({ isViewIndex }) => {
    return (
        <span className={styles['collapse-title']} onClick={event => {
            event.stopPropagation();
        }}>
            {isViewIndex ? <>INDEX EVENT<ToolTip content={INDEX_EVENT} /></> : null}
        </span>
    );
}

export const EditStudyContent: React.FC<{ study: Query }> = ({ study }) => {
    const dispatch = useAppDispatch()
    const activeKeys = useAppSelector(selectActiveKeys)
    const activeKey = activeKeys[study.id]
    const titleRef = useRef<HTMLDivElement | null>(null)
    const location = useLocation()
    const shouldScroll = location?.state?.['fromStudyPeriod'] === true

    const isMounted = useRef(false);

    useEffect(() => {

        if (isMounted.current) {
            dispatch(resetCriteria(study.id));
        } else {
            isMounted.current = true;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [study.dataset]);

    useEffect(() => {
        if (titleRef.current !== null && shouldScroll) {
            titleRef.current.scrollIntoView({
                behavior: 'smooth'
            })
        }
    }, [titleRef, shouldScroll])

    const indexMap = calculateIndexMap(study)
    const displayGroups = study
        .groups
        .map((groupType: GroupType, i: number) => {
            const editableHeader = <><EditableLabel
                className={styles['collapse-title']}
                text={groupType.title}
                onSaveNotification={(value: string) => dispatch(setGroupTitle({ queryId: study.id, groupId: groupType.id, title: value }))}
                dataTestId="event-title"
            />
                {indexMap[groupType.id] ? null : <ToolTip content={EVENT} />}
            </>
            const { index, ...groupTypeNoIndex } = groupType
            return <Panel
                {...groupTypeNoIndex}
                className={i === 0 ? styles.index : styles.subsequent}
                key={groupType.id} header={editableHeader}
                extra={<GenExtra isViewIndex={indexMap[groupType.id]} />}
            >
                <GroupView id={groupType.id} queryId={study.id} criteria={groupType.criteria} deleteDisabled={i === 0 && study.groups.length === 1} />
            </Panel>
        })
        .flatMap(function callback(i: JSX.Element, index: number, array: JSX.Element[]) {
            const operatorChanged = () => {
                return (value: string) => {
                    dispatch(groupOperatorChanged({ queryId: study.id, groupId: i.props.id, value }))
                }
            }
            const id = i.key!.toString()
            const isIndex = indexMap[id] && index === 0
            let message = null
            if (array.length === 1) {
                message = <DefaultMessage key={'default'}/>
            } else if (index > 1) {
                message = <div>Please note that Time Between Event calculations only work between Event 0 and Event 1.</div>
            }
            return [
                i,
                isIndex && array.length > 1 ? <TimeBetweenEvents key={id + "_tbe"} id={id} operator={i.props.operator}/> : message,
                <Operator key={i.key + '_'} currentValue={i.props.operator} showChildren={true} showIcons={true}
                                    onOperatorChanged={operatorChanged} isBetweenEvents={true}/>
            ]
        })

    // remove the last operator
    displayGroups.pop()

    return <>
        <CohortTimeframe key="timeline" />
        <div key="cohort" className={`cohort ${styles['scroll-section']}`} ref={titleRef}>
            <div className="title">
                Build a cohort
                <span className="icon">
                    <Button
                        data-testid="add-event-button"
                        className={buttonStyles.primary}
                        icon={<PlusOutlined className={styles['event-add']} />}
                        type="primary"
                        onClick={() => dispatch(addGroup(study.id))}
                    >
                        Add Event
                        <ToolTip content={ADD_EVENT} />
                    </Button>
                </span>
            </div>
            <Collapse
                activeKey={activeKey}
                onChange={e => {
                    dispatch(setActiveKey({ queryId: study.id, activeKey: e }))
                }}
                expandIconPosition="end"
                expandIcon={({ isActive }) =>
                    isActive ? <MinusOutlined className={styles['edit-icon']} />
                        : <PlusOutlined className={styles['edit-icon']} />
                }
                className={styles.collapse}
            >
                {displayGroups}
            </Collapse>
        </div>
    </>
}